import { useEffect, useState, useRef } from 'react'
import {
  PROCESSING_MESSAGES,
  PROCESSING_STATUS,
  formatElapsedTime,
  type ProcessingStatus,
} from '../../constants/processingMessages'
import { OptimusAvatar } from './OptimusAvatar'
import { Icon } from '@/design-system/atoms/Icon'
import { Spinner } from '@/design-system/atoms/Spinner'

interface ProcessingIndicatorProps {
  status: ProcessingStatus
  startTime?: number // Unix timestamp in ms when processing started
  variant?: 'inline' | 'fullscreen'
  className?: string
  onCancel?: () => void // Callback to cancel processing
}

/**
 * ProcessingIndicator - Unified loading/processing feedback component
 *
 * Features:
 * - Optimus avatar/identity
 * - Status message (Enviando... / Optimus está pensando...)
 * - Elapsed time counter
 * - Two variants: inline (in chat flow) and fullscreen (empty state loading)
 */
export function ProcessingIndicator({
  status,
  startTime,
  variant = 'inline',
  className = '',
  onCancel,
}: ProcessingIndicatorProps) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  // `ReturnType<typeof setInterval>` em vez de `NodeJS.Timeout`: no browser é
  // `number`, e o tipo do Node não existe aqui (só entrava porque este arquivo
  // nunca era checado junto dos testes).
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Calculate and update elapsed time
  useEffect(() => {
    if (!startTime || status === PROCESSING_STATUS.IDLE) {
      setElapsedSeconds(0)
      return
    }

    // Calculate initial elapsed time (important for resuming)
    const calculateElapsed = () => {
      const now = Date.now()
      const elapsed = Math.floor((now - startTime) / 1000)
      setElapsedSeconds(Math.max(0, elapsed))
    }

    // Set initial value
    calculateElapsed()

    // Update every second
    intervalRef.current = setInterval(calculateElapsed, 1000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [startTime, status])

  // Don't render if idle
  if (status === PROCESSING_STATUS.IDLE) {
    return null
  }

  const getMessage = (): string => {
    switch (status) {
      case PROCESSING_STATUS.SENDING:
        return PROCESSING_MESSAGES.SENDING
      case PROCESSING_STATUS.QUEUED:
        return PROCESSING_MESSAGES.QUEUED
      case PROCESSING_STATUS.PROCESSING:
        return PROCESSING_MESSAGES.PROCESSING
      case PROCESSING_STATUS.COMPLETING:
        return PROCESSING_MESSAGES.COMPLETING
      case PROCESSING_STATUS.ERROR:
        return PROCESSING_MESSAGES.ERROR_GENERIC
      default:
        return PROCESSING_MESSAGES.PROCESSING
    }
  }

  // Fullscreen variant - centered with larger elements
  if (variant === 'fullscreen') {
    return (
      <div
        className={`flex flex-col items-center justify-center h-full gap-4 ${className}`}
        role="status"
        aria-live="polite"
        aria-label={getMessage()}
      >
        {/* Optimus Avatar */}
        <div className="relative">
          <OptimusAvatar size="xl" className="shadow-lg" />
          {/* Pulsing ring animation */}
          <div className="absolute inset-0 w-16 h-16 rounded-full bg-brand-500 animate-ping opacity-20" />
        </div>

        {/* Message */}
        <div className="text-center">
          <p className="text-base font-medium text-gray-700 dark:text-gray-200">
            {getMessage()}
          </p>
          {/* Elapsed time - with icon to differentiate from clock time */}
          {startTime && elapsedSeconds > 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-300 mt-1 tabular-nums flex items-center justify-center gap-1">
              <span className="text-xs">⏱️</span>
              {formatElapsedTime(elapsedSeconds)}
            </p>
          )}
          {/* Cancel button - shown after 5 seconds */}
          {onCancel && elapsedSeconds >= 5 && (
            <button
              onClick={onCancel}
              className="mt-3 px-4 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            >
              Cancelar
            </button>
          )}
        </div>
      </div>
    )
  }

  // Inline variant - compact for chat flow
  return (
    <div
      className={`flex items-start gap-3 ${className}`}
      role="status"
      aria-live="polite"
      aria-label={getMessage()}
    >
      {/* Optimus Avatar - smaller for inline */}
      <div className="flex-shrink-0 relative">
        <OptimusAvatar size="md" />
        {/* Small pulsing dot */}
        <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white dark:border-gray-800 animate-pulse" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl rounded-tl-sm px-4 py-3">
          <div className="flex items-center gap-3">
            {/* Spinner */}
            <Spinner size="sm" className="text-brand-500 flex-shrink-0" />

            {/* Message */}
            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
              {getMessage()}
            </span>

            {/* Elapsed time - with icon to differentiate from clock time */}
            {startTime && elapsedSeconds > 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums ml-auto flex items-center gap-1">
                <span>⏱️</span>
                {formatElapsedTime(elapsedSeconds)}
              </span>
            )}

            {/* Cancel button - shown after 5 seconds */}
            {onCancel && elapsedSeconds >= 5 && (
              <button
                onClick={onCancel}
                className="ml-2 p-1 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                title="Cancelar processamento"
                aria-label="Cancelar processamento"
              >
                <Icon name="x" size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProcessingIndicator
